import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const BRANCHES = [
  'Head Office',
  'Otun-Ekiti Meeting Point',
  'Ado-Ekiti Meeting Point',
  'Isan-Ekiti Branch',
  'Igede-Ekiti Meeting Point',
  'Ijero-Ekiti Meeting Point',
  'Ilupeju-Ekiti Branch',
];

const CATEGORIES_ICT = [
  'App Profile Lock',
  'ATM Transaction Error',
  'ATM Card Request',
  'Network Error',
  'Others',
];

const CATEGORIES_FINANCE = [
  'Payment Processing Error',
  'Account Reconciliation',
  'Transfer Issue',
  'Others',
];

export default function NewTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCareRep = user?.role === 'care_rep';

  const [department, setDepartment] = useState('ict');
  const [category, setCategory] = useState('');
  const [otherCategory, setOtherCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [branch, setBranch] = useState(user?.branch || '');
  const [affectedStaff, setAffectedStaff] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const categories = department === 'finance' ? CATEGORIES_FINANCE : CATEGORIES_ICT;
  const isATMCard = category === 'ATM Card Request';

  const handleDeptChange = (dept) => {
    setDepartment(dept);
    setCategory('');
    setOtherCategory('');
  };

  const handleSubmit = async () => {
    if (!subject.trim()) return toast.error('Please enter a subject');
    if (!description.trim()) return toast.error('Please enter a description');
    if (!category) return toast.error('Please select a category');
    if (!branch) return toast.error('Please select a branch');
    if (category === 'Others' && !otherCategory.trim()) return toast.error('Please specify the category');
    if (isATMCard && files.length === 0) return toast.error('Please attach the card request document');

    setLoading(true);
    try {
      const data = new FormData();
      const finalCategory = category === 'Others' ? 'Others: ' + otherCategory : category;
      data.append('subject', subject);
      data.append('description', description);
      data.append('category', finalCategory);
      data.append('priority', 'medium');
      data.append('branch', branch);
      data.append('department', department);
      if (affectedStaff) data.append('affected_staff', affectedStaff);
      files.forEach(f => data.append('attachments', f));

      const res = await api.post('/api/tickets', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Ticket ' + res.data.ticket.ticket_number + ' submitted! Confirmation email sent.');
      navigate('/tickets/' + res.data.ticket.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 14 }} onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="form-card">
        <div className="form-card-title">Lodge a New Support Ticket</div>
        <form onSubmit={e => e.preventDefault()}>

          {/* Department Selector */}
          <div className="form-group">
            <label className="form-label">Which department is this for? <span className="req">*</span></label>
            <div style={{ display: 'flex', gap: 10 }}>
              <div
                onClick={() => handleDeptChange('ict')}
                style={{
                  flex: 1, padding: '12px 14px', borderRadius: 'var(--radius)',
                  border: '2px solid ' + (department === 'ict' ? 'var(--sky)' : 'var(--border)'),
                  background: department === 'ict' ? 'var(--sky-light)' : 'var(--white)',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: department === 'ict' ? 'var(--sky2)' : 'var(--text)', marginBottom: 3 }}>
                  💻 ICT Department
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Technical issues, network, app problems, ATM card requests
                </div>
              </div>
              <div
                onClick={() => handleDeptChange('finance')}
                style={{
                  flex: 1, padding: '12px 14px', borderRadius: 'var(--radius)',
                  border: '2px solid ' + (department === 'finance' ? 'var(--sky)' : 'var(--border)'),
                  background: department === 'finance' ? 'var(--sky-light)' : 'var(--white)',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: department === 'finance' ? 'var(--sky2)' : 'var(--text)', marginBottom: 3 }}>
                  💰 Finance Department
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Payments, accounts, financial queries
                </div>
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Category <span className="req">*</span></label>
            <select className="form-select" value={category} onChange={e => setCategory(e.target.value)} required>
              <option value="">Select category…</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {category === 'Others' && (
              <input className="form-input" style={{ marginTop: 8 }} placeholder="Please specify…"
                value={otherCategory} onChange={e => setOtherCategory(e.target.value)} />
            )}
          </div>

          {/* ATM Card Request info */}
          {isATMCard && (
            <div style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderLeft: '3px solid var(--warn)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--warn)', marginBottom: 6 }}>🏧 ATM Card Request</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                Please upload the card request document as an attachment below. The ICT team will process and notify you when ready.
                <br /><strong>Note:</strong> This request has no SLA — processing time depends on card production schedule.
              </div>
            </div>
          )}

          {/* Branch and Affected Staff */}
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Branch <span className="req">*</span></label>
              {isCareRep ? (
                <div style={{ padding: '8px 12px', background: 'var(--sky-light)', border: '1.5px solid var(--sky-mid)', borderRadius: 'var(--radius-sm)', fontSize: 13, color: 'var(--sky2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  🏢 {user?.branch} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(your branch)</span>
                </div>
              ) : (
                <select className="form-select" value={branch} onChange={e => setBranch(e.target.value)} required>
                  <option value="">Select branch…</option>
                  {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">{isATMCard ? 'Number of Cards' : 'Affected Staff / System'}</label>
              <input className="form-input"
                placeholder={isATMCard ? 'e.g. 5 cards' : 'e.g. Ada Obi / Terminal 3'}
                value={affectedStaff} onChange={e => setAffectedStaff(e.target.value)} />
            </div>
          </div>

          {/* Subject */}
          <div className="form-group">
            <label className="form-label">Subject <span className="req">*</span></label>
            <input className="form-input"
              placeholder={isATMCard ? 'e.g. ATM Card Request — Head Office (5 cards)' : 'Brief title of the issue'}
              value={subject} onChange={e => setSubject(e.target.value)} required />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">
              {isATMCard ? 'Additional Notes' : 'Detailed Description'} <span className="req">*</span>
            </label>
            <textarea className="form-textarea" style={{ minHeight: 100 }}
              placeholder={isATMCard
                ? 'Any special instructions for this card request…'
                : 'Describe what happened, when it started, steps already tried…'}
              value={description} onChange={e => setDescription(e.target.value)} required />
          </div>

          {/* Attachments */}
          <div className="form-group">
            <label className="form-label">
              {isATMCard ? 'Card Request Document' : 'Attachments'}
              {isATMCard
                ? <span className="req"> * (required)</span>
                : <span style={{ color: 'var(--text3)', fontWeight: 400 }}> (optional)</span>}
            </label>
            <div className="upload-zone" onClick={() => document.getElementById('file-input').click()}
              style={{ borderColor: isATMCard && files.length === 0 ? 'var(--warn)' : undefined }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>📎</div>
              <div>{isATMCard ? 'Click to upload card request document (required)' : 'Click to browse or drag & drop files'}</div>
              <div style={{ marginTop: 4, fontSize: 11 }}>Images, PDFs, Word docs — max 10MB each, up to 5 files</div>
              {files.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {files.map((f, i) => (
                    <span key={i} style={{ fontSize: 11, color: 'var(--sky2)', background: 'var(--sky-light)', display: 'inline-block', padding: '2px 8px', borderRadius: 4, margin: 2 }}>
                      📄 {f.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <input id="file-input" type="file" multiple
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              style={{ display: 'none' }} onChange={e => setFiles([...e.target.files])} />
          </div>

          {!isATMCard && (
            <div className="info-box" style={{ marginBottom: 14 }}>
              ℹ️ {department === 'finance' ? 'Finance team' : 'ICT team'} will be notified immediately by email.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Submitting…' : '🚀 Submit Ticket'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => navigate(-1)}>Cancel</button>
          </div>

        </form>
      </div>
    </div>
  );
}
